// Practice — Diagnostics. Scored self-assessments mapped onto the real
// frameworks. Each returns a position on a model plus two or three concrete
// moves — never a pass/fail. Pure scoring logic shared by client and worker.

export interface Scale {
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
}

export interface DiagQuestion {
  id: string;
  text: string;
  dim: string; // which dimension this question loads onto
  reverse?: boolean; // high answer = low dimension
}

export interface Outcome {
  label: string;
  summary: string;
  moves: string[];
}

export type ResultKind = "min" | "max" | "quad";

export interface QuadResolve {
  xDim: string;
  yDim: string;
  mid: number;
  // keys: high-x/high-y, high-x/low-y, low-x/high-y, low-x/low-y
  hiHi: string;
  hiLo: string;
  loHi: string;
  loLo: string;
}

export interface Diagnostic {
  id: string;
  title: string;
  frameworkId: string;
  theme: string;
  faculty: string;
  estimate: string;
  intro: string;
  scale: Scale;
  questions: DiagQuestion[];
  resultKind: ResultKind;
  quad?: QuadResolve;
  outcomes: Record<string, Outcome>;
}

export interface DiagResult {
  dimScores: Record<string, number>;
  outcomeKey: string;
}

const DEFAULT_SCALE: Scale = { min: 1, max: 5, minLabel: "Rarely / strongly disagree", maxLabel: "Almost always / strongly agree" };

export function scoreDiagnostic(d: Diagnostic, answers: Record<string, number>): DiagResult {
  const sums: Record<string, number> = {};
  const counts: Record<string, number> = {};
  for (const q of d.questions) {
    let v = answers[q.id];
    if (v == null) continue;
    if (q.reverse) v = d.scale.max + d.scale.min - v;
    sums[q.dim] = (sums[q.dim] || 0) + v;
    counts[q.dim] = (counts[q.dim] || 0) + 1;
  }
  const dimScores: Record<string, number> = {};
  for (const k of Object.keys(sums)) dimScores[k] = sums[k] / counts[k];

  let outcomeKey = "";
  if (d.resultKind === "quad" && d.quad) {
    const x = dimScores[d.quad.xDim] ?? 0;
    const y = dimScores[d.quad.yDim] ?? 0;
    const hiX = x >= d.quad.mid;
    const hiY = y >= d.quad.mid;
    outcomeKey = hiX ? (hiY ? d.quad.hiHi : d.quad.hiLo) : hiY ? d.quad.loHi : d.quad.loLo;
  } else {
    const keys = Object.keys(dimScores);
    if (keys.length) {
      outcomeKey = keys.reduce((best, k) => {
        if (d.resultKind === "max") return dimScores[k] > dimScores[best] ? k : best;
        return dimScores[k] < dimScores[best] ? k : best; // "min"
      }, keys[0]);
    }
  }
  return { dimScores, outcomeKey };
}

export const DIAGNOSTICS: Diagnostic[] = [
  {
    id: "psych-safety",
    title: "Where does your team's safety break down?",
    frameworkId: "clark",
    theme: "Psychological safety",
    faculty: "Amanda Gethi",
    estimate: "~3 min · private",
    intro: "Clark's four stages build in sequence. Answer for your team as it actually is, and this points to the stage to work on next — the lowest is where the work is.",
    scale: DEFAULT_SCALE,
    resultKind: "min",
    questions: [
      { id: "ps1", text: "People on my team feel they belong and can be themselves here.", dim: "Inclusion" },
      { id: "ps2", text: "New or junior people are included in the real conversations, not just the formal ones.", dim: "Inclusion" },
      { id: "ps3", text: "People freely ask questions and admit when they don't know something.", dim: "Learner" },
      { id: "ps4", text: "Mistakes are surfaced and learned from rather than hidden.", dim: "Learner" },
      { id: "ps5", text: "People feel their skills and contributions are genuinely welcomed.", dim: "Contributor" },
      { id: "ps6", text: "People are trusted to act and add value without having to ask permission.", dim: "Contributor" },
      { id: "ps7", text: "People challenge the status quo — including my decisions — without it costing them.", dim: "Challenger" },
      { id: "ps8", text: "Someone has openly disagreed with me in a meeting in the last month.", dim: "Challenger" },
    ],
    outcomes: {
      Inclusion: {
        label: "Inclusion safety is your weakest stage",
        summary: "Before anything else, some people don't yet feel they fully belong — and you can't build the higher stages on top of that.",
        moves: [
          "Notice who stays quiet in the real conversations, and bring them in by name.",
          "Go first with a bit of your own humanity before asking others to.",
          "Check that 'how we do things' isn't quietly excluding people from different backgrounds.",
        ],
      },
      Learner: {
        label: "Learner safety is your weakest stage",
        summary: "People belong, but asking questions or admitting a gap still feels risky — so learning and error-surfacing suffer.",
        moves: [
          "Reward the question and the admitted mistake visibly, especially your own.",
          "Replace 'any questions?' with a designed prompt that makes asking the norm.",
          "Treat a near-miss as a learning audit, not a search for who to blame.",
        ],
      },
      Contributor: {
        label: "Contributor safety is your weakest stage",
        summary: "People learn but don't feel fully trusted to act — so good work waits for permission.",
        moves: [
          "Hand over a real decision with clear boundaries rather than hovering.",
          "Credit contributions specifically and publicly.",
          "Ask what's stopping people acting without checking with you first.",
        ],
      },
      Challenger: {
        label: "Challenger safety is your weakest stage",
        summary: "The most common plateau: people contribute well but won't question the status quo — which is exactly where dissent, early warnings and innovation come from.",
        moves: [
          "Go second — invite the dissent before stating your own view.",
          "Thank the person who challenges you, in the moment, so others see it's safe.",
          "Name it out loud in a retro: 'are we contributing, or are we challenging?'",
        ],
      },
    },
  },
  {
    id: "speaking-up",
    title: "Why isn't this getting said?",
    frameworkId: "truth",
    theme: "Courage & candour",
    faculty: "Dr Megan Reitz",
    estimate: "~3 min · private",
    intro: "Bring one recent silence to mind — something you didn't say, or didn't hear. TRUTH names five factors that suppress voice; this surfaces the one most at play for you.",
    scale: { min: 1, max: 5, minLabel: "Not at all", maxLabel: "Very much so" },
    resultKind: "max",
    questions: [
      { id: "su1", text: "I doubted my own opinion was worth voicing.", dim: "Trust" },
      { id: "su2", text: "I expected a real cost — being judged, or the problem becoming mine.", dim: "Risk" },
      { id: "su3", text: "I wasn't sure how the politics or power in the room would read it.", dim: "Understanding" },
      { id: "su4", text: "Status — seniority, tenure, who's in the in-group — kept me (or them) quiet.", dim: "Titles" },
      { id: "su5", text: "The timing, place or forum was wrong for it to land.", dim: "How" },
    ],
    outcomes: {
      Trust: {
        label: "Trust is the dominant factor",
        summary: "Voice stalled on whether the opinion was even worth trusting — yours or theirs.",
        moves: [
          "Say the half-formed point anyway; you were probably hired for exactly that difference.",
          "When someone hedges, ask for the version they actually believe.",
          "Notice the recruitment trap: difference hired, then quietly rewarded for compliance.",
        ],
      },
      Risk: {
        label: "Risk is the dominant factor",
        summary: "The anticipated cost of speaking — judgement, or being handed the problem — outweighed the benefit.",
        moves: [
          "Separate raising a problem from owning it; say so explicitly.",
          "Watch your response in the first few seconds — that's where the real risk is set.",
          "Test whether the feared reaction is fantasised or actually evidenced.",
        ],
      },
      Understanding: {
        label: "Understanding is the dominant factor",
        summary: "Uncertainty about the politics and power in the room governed what got said.",
        moves: [
          "Name the unwritten rules out loud so they stop doing the silencing.",
          "Remember seniority distances you from the ground truth — go and get it.",
          "Map who really influences the decision before the conversation.",
        ],
      },
      Titles: {
        label: "Titles & labels are the dominant factor",
        summary: "Status — yours or theirs — shaped who felt able to speak. This is advantage blindness at work.",
        moves: [
          "Name the labels you carry that you've stopped noticing.",
          "Go second and flatten the status cue before asking for views.",
          "Ask the people your labels intimidate directly, not in the open room.",
        ],
      },
      How: {
        label: "How is the dominant factor",
        summary: "The skill, timing, place or forum was wrong — the point was sound but the design wasn't.",
        moves: [
          "Redesign the conversation with the five Ws: who, why, what, where, when.",
          "Move a hard conversation to a smaller room, a walk, or a shared-in-advance question.",
          "Give people a concrete role ('argue as our toughest customer') so candour is easy.",
        ],
      },
    },
  },
  {
    id: "positioning",
    title: "Owl, fox, donkey or sheep?",
    frameworkId: "baddeley",
    theme: "Influence & politics",
    faculty: "John Denton",
    estimate: "~3 min · private",
    intro: "Two axes — how aware you are of the politics, and whether you act with integrity for the organisation. Answer honestly for a specific room that matters; the aim is the owl.",
    scale: { min: 1, max: 5, minLabel: "Strongly disagree", maxLabel: "Strongly agree" },
    resultKind: "quad",
    quad: { xDim: "awareness", yDim: "integrity", mid: 3, hiHi: "owl", hiLo: "fox", loHi: "sheep", loLo: "donkey" },
    questions: [
      { id: "po1", text: "I read the interests, alliances and unspoken agendas in the room accurately.", dim: "awareness" },
      { id: "po2", text: "I know who really influences the decisions that matter, beyond the org chart.", dim: "awareness" },
      { id: "po3", text: "I socialise a proposal with the right people before the meeting.", dim: "awareness" },
      { id: "po4", text: "My moves serve the organisation's interest, not just my own standing.", dim: "integrity" },
      { id: "po5", text: "I'd make the same case whether or not it advantaged me personally.", dim: "integrity" },
      { id: "po6", text: "People would describe my political behaviour as principled.", dim: "integrity" },
    ],
    outcomes: {
      owl: {
        label: "Owl — aware and principled",
        summary: "You read the politics clearly and act with integrity. The aim is to stay here and bring others up to it.",
        moves: [
          "Carry the trickiest conversations others can't, and model how it's done.",
          "Use your read of the room in service of a worthwhile end, openly.",
          "Mentor a 'sheep' you rate — good intent, low awareness — into the politics.",
        ],
      },
      fox: {
        label: "Fox — aware but self-serving",
        summary: "You read the politics well, but the energy is going into your own standing more than the organisation's interest.",
        moves: [
          "Redirect the same awareness toward a genuinely shared win.",
          "Ask whose interest each move actually serves before you make it.",
          "Notice the emotional cost your manoeuvres leave behind.",
        ],
      },
      sheep: {
        label: "Sheep — principled but politically naive",
        summary: "Your intentions are sound but you're under-networked — good proposals risk dying because they walk in cold.",
        moves: [
          "Socialise the paper before the meeting, pitched to each character.",
          "Map the stakeholders and who influences them before you present.",
          "Find an owl to carry or sponsor your toughest conversation.",
        ],
      },
      donkey: {
        label: "Donkey — low awareness, low integrity",
        summary: "A blunt result, and rarely the whole story — but worth sitting with: neither the read of the room nor the principled intent is landing right now.",
        moves: [
          "Slow down and read interests before acting; ask more, assert less.",
          "Check whether a 'fox' is setting you up to take a fall.",
          "Reconnect each move to the organisation's interest, not just your own.",
        ],
      },
    },
  },
  {
    id: "change-readiness",
    title: "Is your change actually movable?",
    frameworkId: "change-equation",
    theme: "Leading change",
    faculty: "John Denton",
    estimate: "~2 min · private",
    intro: "Resistance shifts only when dissatisfaction, vision and next steps multiply to outweigh it. Score a stalled change honestly — because the factors multiply, your lowest is what's killing it.",
    scale: { min: 1, max: 5, minLabel: "Not at all true", maxLabel: "Completely true" },
    resultKind: "min",
    questions: [
      { id: "cr1", text: "People are genuinely dissatisfied with how things are now.", dim: "Dissatisfaction" },
      { id: "cr2", text: "The pain of the status quo is felt, not just acknowledged on a slide.", dim: "Dissatisfaction" },
      { id: "cr3", text: "There's a clear, compelling picture of the future we're moving to.", dim: "Vision" },
      { id: "cr4", text: "People can describe what 'better' actually looks like.", dim: "Vision" },
      { id: "cr5", text: "There are concrete, credible first steps people can take now.", dim: "Next steps" },
      { id: "cr6", text: "The very next action is obvious and someone owns it.", dim: "Next steps" },
    ],
    outcomes: {
      Dissatisfaction: {
        label: "Dissatisfaction is your weakest factor",
        summary: "Nobody's moving because the status quo doesn't hurt enough — no amount of vision will pull a contented team.",
        moves: [
          "Make the cost of staying the same vivid and concrete, not abstract.",
          "Surface the data people are quietly ignoring.",
          "Don't argue resistance down; raise dissatisfaction and shrink the first step.",
        ],
      },
      Vision: {
        label: "Vision is your weakest factor",
        summary: "People might be dissatisfied and able to act, but there's nowhere clear to go — so effort scatters.",
        moves: [
          "Make the future graspable and concrete, the way a moonshot is sized against something familiar.",
          "Describe the change as toward something positive, not just away from pain.",
          "Get people to describe 'better' in their own words and listen for the gaps.",
        ],
      },
      "Next steps": {
        label: "Next steps are your weakest factor",
        summary: "The dissatisfaction and vision are there, but it stays a dream because there's no credible first move.",
        moves: [
          "Define one small, dated, owned first step and make it visible.",
          "Pair the vision with a pilot so it doesn't float free of action.",
          "Lower the bar to start — make the next action almost too small to refuse.",
        ],
      },
    },
  },
  {
    id: "grip-check",
    title: "GRIP — where is your team stuck?",
    frameworkId: "grip",
    theme: "Thriving teams",
    faculty: "John Denton",
    estimate: "~3 min · private",
    intro: "Teams reach for an interpersonal explanation ('we don't get on') when the real fault is higher up. Score your team on goals, roles, interpersonal trust and processes — the weakest is where to start.",
    scale: { min: 1, max: 5, minLabel: "Strongly disagree", maxLabel: "Strongly agree" },
    resultKind: "min",
    questions: [
      { id: "gr1", text: "We share a clear goal and everyone could state it the same way.", dim: "Goals" },
      { id: "gr2", text: "Our objectives are aligned, not quietly pulling in different directions.", dim: "Goals" },
      { id: "gr3", text: "Roles and accountabilities are clear — people know their lane.", dim: "Roles" },
      { id: "gr4", text: "Hand-offs and boundaries between people are explicit.", dim: "Roles" },
      { id: "gr5", text: "There's openness, honesty and trust — it's safe to take a risk.", dim: "Interpersonal" },
      { id: "gr6", text: "People raise tensions directly rather than letting them fester.", dim: "Interpersonal" },
      { id: "gr7", text: "Our processes — decisions, planning, communication — are fit for purpose.", dim: "Processes" },
      { id: "gr8", text: "Conflict gets resolved through a process, not personalities.", dim: "Processes" },
    ],
    outcomes: {
      Goals: {
        label: "Goals are your weakest layer",
        summary: "What looks like friction is often an unclear or misaligned goal — fix the top of the list first.",
        moves: [
          "Get everyone to write the goal independently and compare — the gaps are the problem.",
          "Align objectives explicitly before working on relationships.",
          "Restate the shared goal at the start of the work that matters.",
        ],
      },
      Roles: {
        label: "Roles are your weakest layer",
        summary: "Ambiguous accountabilities breed territory fights — good fences make good neighbours.",
        moves: [
          "Write a one-line responsibility for each person and test it with the team.",
          "Make hand-offs and boundaries explicit where they currently overlap.",
          "Treat role clarity as freeing, not constraining — name who owns what.",
        ],
      },
      Interpersonal: {
        label: "Interpersonal trust is your weakest layer",
        summary: "The foundation everything else stands on is thin — and process bolted onto low trust just adds friction.",
        moves: [
          "Lower your own self-orientation: give others the floor, own the failures.",
          "Build in time for the relational conversation, not just the task.",
          "Make it safe to raise a tension directly before it festers.",
        ],
      },
      Processes: {
        label: "Processes are your weakest layer",
        summary: "Goals, roles and trust are sound, but how you decide, plan and resolve conflict is letting the team down.",
        moves: [
          "Name how decisions actually get made — and fix the step that's missing.",
          "Give conflict a process so it doesn't default to personalities.",
          "Check the meeting and communication rhythm is fit for the work.",
        ],
      },
    },
  },
];
